import { Fragment } from "react";
import { useTheme } from "next-themes";
import { Moon, Sun, PanelLeft, HelpCircle, ListChecks, Play, Sparkles, RotateCcw } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { NotificationBell } from "./NotificationBell";
import { useSidebar } from "@/shared/components/ui/sidebar";
import { Separator } from "@/shared/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { useOnboarding } from "@/shared/components/onboarding/OnboardingProvider";
import { tourRegistry } from "@/shared/components/onboarding/tours";

const BREADCRUMB_I18N_KEYS: Record<string, string> = {
  "dashboard": "nav.dashboard",
  "locations": "nav.locations",
  "assets": "nav.assets",
  "work-orders": "nav.workOrders",
  "pm-schedules": "nav.pmSchedules",
  "checklists": "nav.checklists",
  "templates": "nav.templates",
  "inspections": "nav.inspections",
  "inspection-schedules": "nav.inspectionSchedules",
  "actions": "nav.actions",
  "inventory": "nav.inventory",
  "vendors": "nav.vendors",
  "reports": "nav.reports",
  "analytics": "nav.analytics",
  "incidents": "nav.incidents",
  "meter-readings": "nav.meterReadings",
  "escalation-rules": "nav.escalationRules",
  "users": "nav.users",
  "settings": "nav.settings",
  "audit-log": "nav.auditLog",
  "custom-fields": "nav.customFields",
  "labor-tracking": "nav.laborTracking",
  "integrations": "nav.integrations",
};

export function Header() {
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const { t } = useTranslation("common");
  const { isMobile, openMobile, toggleSidebar } = useSidebar();
  const { startTour, startModuleTour, showChecklist, isTourCompleted, resetOnboarding } = useOnboarding();

  // Detect module tour based on current route
  const currentModule = location.pathname.split("/").filter(Boolean)[0] ?? "";
  const moduleTourId = currentModule === "work-orders" ? "work-orders"
    : currentModule === "pm-schedules" ? "pm-schedules"
    : tourRegistry[currentModule] ? currentModule
    : null;
  const moduleTour = moduleTourId ? tourRegistry[moduleTourId] : null;
  const moduleTourCompleted = moduleTourId ? isTourCompleted(moduleTourId) : false;
  const welcomeCompleted = isTourCompleted("welcome");

  const moduleLabel = currentModule
    ? currentModule.charAt(0).toUpperCase() + currentModule.slice(1).replace(/-/g, " ")
    : "";

  // Build breadcrumbs from location.pathname
  const pathnames = location.pathname.split("/").filter((x) => x);

  // Format labels using i18n keys and handle UUID-type segments
  const formatLabel = (segment: string) => {
    // Basic UUID hex check to label it as "Details" if random ID
    const isId = segment.length > 8 && /^[0-9a-fA-F-]+$/.test(segment);
    if (isId) return t("sidebar.details", "Details");

    const i18nKey = BREADCRUMB_I18N_KEYS[segment];
    if (i18nKey) return t(i18nKey);

    return segment.charAt(0).toUpperCase() + segment.slice(1);
  };

  // Right-side title (current active segment or APP_NAME)
  const currentSegment = pathnames.length > 0 ? pathnames[pathnames.length - 1] : "/";
  const rightTitle = formatLabel(currentSegment);

  return (
    <header className="sticky top-0 z-30 flex h-14 min-w-0 items-center justify-between border-b bg-background px-4 sm:px-6">
      <div className="flex min-w-0 items-center gap-2">
        {(!isMobile || !openMobile) && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={toggleSidebar}
            aria-label={isMobile ? "Open navigation menu" : "Toggle Sidebar"}
          >
            <PanelLeft className="size-4" />
          </Button>
        )}
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb className="hidden sm:block">
          <BreadcrumbList>
            {pathnames.length === 0 ? (
              <BreadcrumbItem>
                <BreadcrumbPage>{t("nav.dashboard")}</BreadcrumbPage>
              </BreadcrumbItem>
            ) : (
              pathnames.map((value, index) => {
                const to = `/${pathnames.slice(0, index + 1).join("/")}`;
                const isLast = index === pathnames.length - 1;
                const label = formatLabel(value);

                return (
                  <Fragment key={to}>
                    <BreadcrumbItem>
                      {isLast ? (
                        <BreadcrumbPage>{label}</BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink asChild>
                          <Link to={to}>{label}</Link>
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                    {!isLast && <BreadcrumbSeparator />}
                  </Fragment>
                );
              })
            )}
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <div className="flex items-center gap-2">
        <span className="truncate text-sm font-semibold max-w-[150px] sm:max-w-none text-muted-foreground hidden lg:block mr-2">
           {rightTitle !== t("nav.dashboard") && rightTitle}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Help"
              data-tour="help-button"
              className="hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72 p-0">
            {/* Welcome Tour — prominent CTA */}
            <div className="p-2">
              <button
                type="button"
                onClick={() => startTour("welcome")}
                className="group flex w-full items-start gap-3 rounded-lg p-3 text-left transition-all duration-150 bg-primary/10 hover:bg-primary/15 dark:bg-primary/15 dark:hover:bg-primary/20"
              >
                <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/20 text-primary transition-colors group-hover:bg-primary/30">
                  <Sparkles className="size-4" />
                </span>
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-foreground">
                    {t("onboarding.header.welcomeTour", "Welcome Tour")}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {welcomeCompleted
                      ? t("onboarding.header.welcomeTourCompletedDesc", "Take the app tour again")
                      : t("onboarding.header.welcomeTourDesc", "Get a quick overview of Veltro")}
                  </span>
                  {welcomeCompleted && (
                    <span className="mt-0.5 text-[11px] font-medium text-green-600 dark:text-green-400">
                      {t("onboarding.header.completed", "Completed")}
                    </span>
                  )}
                </div>
              </button>
            </div>

            {/* Module tour — contextual */}
            {moduleTour && moduleTourId && (
              <>
                <DropdownMenuSeparator className="my-0" />
                <div className="p-2">
                  <button
                    type="button"
                    onClick={() => startModuleTour(moduleTourId)}
                    className="group flex w-full items-start gap-3 rounded-lg p-3 text-left transition-all duration-150 hover:bg-accent dark:hover:bg-accent/50"
                  >
                    <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground transition-colors group-hover:bg-accent-foreground/20 group-hover:text-accent-foreground">
                      <Play className="size-4" />
                    </span>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium text-foreground group-hover:text-accent-foreground">
                        {t("onboarding.header.moduleTour", {
                          module: moduleLabel,
                          defaultValue: `${moduleLabel} Tour`,
                        })}
                      </span>
                      <span className="text-xs text-muted-foreground group-hover:text-accent-foreground/70">
                        {t("onboarding.header.moduleTourDesc", {
                          module: moduleLabel.toLowerCase(),
                          defaultValue: `Learn how to use ${moduleLabel.toLowerCase()}`,
                        })}
                      </span>
                      {moduleTourCompleted && (
                        <span className="mt-0.5 text-[11px] font-medium text-green-600 dark:text-green-400">
                          {t("onboarding.header.completed", "Completed")}
                        </span>
                      )}
                    </div>
                  </button>
                </div>
              </>
            )}

            {/* Checklist shortcut */}
            <DropdownMenuSeparator className="my-0" />
            <div className="p-2">
              <button
                type="button"
                onClick={() => showChecklist()}
                className="group flex w-full items-start gap-3 rounded-lg p-3 text-left transition-all duration-150 hover:bg-accent dark:hover:bg-accent/50"
              >
                <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground transition-colors group-hover:bg-accent-foreground/20 group-hover:text-accent-foreground">
                  <ListChecks className="size-4" />
                </span>
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-foreground group-hover:text-accent-foreground">
                    {t("onboarding.header.checklist", "Getting Started Checklist")}
                  </span>
                  <span className="text-xs text-muted-foreground group-hover:text-accent-foreground/70">
                    {t("onboarding.header.checklistDesc", "Track your setup progress")}
                  </span>
                </div>
              </button>
            </div>

            {/* Reset progress */}
            <DropdownMenuSeparator className="my-0" />
            <div className="p-2">
              <button
                type="button"
                onClick={() => resetOnboarding()}
                className="group flex w-full items-start gap-3 rounded-lg p-3 text-left transition-all duration-150 hover:bg-accent dark:hover:bg-accent/50"
              >
                <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground transition-colors group-hover:bg-accent-foreground/20 group-hover:text-accent-foreground">
                  <RotateCcw className="size-4" />
                </span>
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-foreground group-hover:text-accent-foreground">
                    {t("onboarding.header.resetProgress", "Reset Progress")}
                  </span>
                  <span className="text-xs text-muted-foreground group-hover:text-accent-foreground/70">
                    {t("onboarding.header.resetProgressDesc", "Start the onboarding from scratch")}
                  </span>
                </div>
              </button>
            </div>

            {/* Help label */}
            <DropdownMenuSeparator className="my-0" />
            <DropdownMenuLabel className="px-4 py-2 text-center">
              <span className="text-[11px] text-muted-foreground">
                {t("onboarding.header.helpHint", "Press ? anytime for help")}
              </span>
            </DropdownMenuLabel>
          </DropdownMenuContent>
        </DropdownMenu>
        <NotificationBell />
        <LanguageSwitcher />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          aria-label={t("theme.toggle")}
          className="hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          {theme === "dark" ? (
            <Sun className="h-4 w-4" />
          ) : (
            <Moon className="h-4 w-4" />
          )}
        </Button>
      </div>
    </header>
  );
}
